import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Search, Paperclip, ChevronDown } from "lucide-react";
import Modal from "../components/Modal";
import SearchableDropdown from "../components/SearchableDropdown";
import ImageUpload from "@/components/ImageUpload";
import { useToast } from "@/context/ToastContext";
import { expensesApi, eventsApi, vendorsApi, apiErrorMessage, type ApiError } from "@/lib/api";
import { formatCurrencyFull, formatDate, formatDateForInput, localToday } from "@/lib/data";
import { getImageUrl } from "@/lib/utils";
import {
  type ExpenseCategoryOption,
  type ExpenseFormOptions,
  type ExpenseRecord,
  type PaymentMethodOption,
  defaultExpenseTitle,
  listFrom,
  localDateOffset,
  normalizeCategoryName,
  paymentMethodLabel,
  referenceFieldFor,
} from "./expenseUtils";

interface ExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Expense being edited; omit to add a new one */
  expense?: ExpenseRecord | null;
  /** Pre-selects the event (e.g. when opened from an event page) */
  defaultEventId?: number;
  onExpenseSaved: () => void;
}

interface EventOption { id: number; name: string; start_date?: string }
interface VendorOption { id: number; name: string }

const CHIP_COUNT = 6;
const FIELD_KEYS: Record<string, string> = {
  amount: "amount",
  expense_category_id: "category",
  expense_date: "date",
  proof_file: "receipt",
  payment_method: "method",
  event_id: "event",
  title: "title",
};

export default function ExpenseModal({ isOpen, onClose, expense, defaultEventId, onExpenseSaved }: ExpenseModalProps) {
  const { showToast } = useToast();
  const isEdit = !!expense?.id;
  const amountRef = useRef<HTMLInputElement>(null);

  const [options, setOptions] = useState<ExpenseFormOptions>({ categories: [], payment_methods: [], receipt_required_above: 0 });
  const [events, setEvents] = useState<EventOption[]>([]);
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const [amount, setAmount] = useState("");
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [date, setDate] = useState(localToday());
  const [eventId, setEventId] = useState("");
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [paidTo, setPaidTo] = useState("");
  const [receipt, setReceipt] = useState<File | null>(null);
  const [note, setNote] = useState("");
  const [title, setTitle] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [catSearch, setCatSearch] = useState("");
  const [catListOpen, setCatListOpen] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Load the form's lists every time it opens (categories may have been added elsewhere)
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setOptionsLoading(true);
    Promise.all([expensesApi.formOptions(), eventsApi.list(), vendorsApi.list("for_select=1")])
      .then(([optRes, evRes, vRes]) => {
        if (cancelled) return;
        const opt = optRes?.data || {};
        setOptions({
          categories: opt.categories || [],
          payment_methods: opt.payment_methods || [],
          receipt_required_above: Number(opt.receipt_required_above) || 0,
        });
        setEvents(
          listFrom<EventOption>(evRes).sort((a, b) => (b.start_date || "").localeCompare(a.start_date || ""))
        );
        setVendors(listFrom<VendorOption>(vRes));
      })
      .catch((err) => showToast(apiErrorMessage(err, "Couldn't load the expense form"), "error"))
      .finally(() => !cancelled && setOptionsLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Fill the form when it opens
  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setReceipt(null);
    setCatSearch("");
    setCatListOpen(false);
    if (expense) {
      const catName = expense.category?.name;
      const wasAuto = expense.title === defaultExpenseTitle(catName, expense.paid_to) || expense.title === "Expense";
      setAmount(expense.amount ? String(Number(expense.amount)) : "");
      setCategoryId(expense.expense_category_id ?? null);
      setDate(expense.expense_date ? formatDateForInput(expense.expense_date) : localToday());
      setEventId(expense.event_id ? String(expense.event_id) : "");
      setMethod(expense.payment_method || "cash");
      setReference(expense.reference_number || "");
      setPaidTo(expense.paid_to || expense.vendor?.name || "");
      setNote(expense.description || "");
      setTitle(wasAuto ? "" : expense.title || "");
      setInternalNotes(expense.notes || "");
    } else {
      setAmount("");
      setCategoryId(null);
      setDate(localToday());
      setEventId(defaultEventId ? String(defaultEventId) : "");
      setMethod("cash");
      setReference("");
      setPaidTo("");
      setNote("");
      setTitle("");
      setInternalNotes("");
      setTimeout(() => amountRef.current?.focus(), 50);
    }
  }, [isOpen, expense, defaultEventId]);

  // Default to Cash when the master list has it, otherwise its first method
  useEffect(() => {
    if (isEdit || !options.payment_methods.length) return;
    if (!options.payment_methods.some((m) => m.value === method)) {
      setMethod(options.payment_methods.find((m) => m.value.includes("cash"))?.value || options.payment_methods[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.payment_methods]);

  const categories = options.categories;
  const selectedCategory = categories.find((c) => c.id === categoryId) || (expense?.category && expense.category.id === categoryId ? expense.category : undefined);

  // Most-used categories as one-tap chips; the selected one is always visible
  const chipCategories = useMemo(() => {
    const top = categories.slice(0, CHIP_COUNT);
    if (selectedCategory && !top.some((c) => c.id === selectedCategory.id)) return [...top.slice(0, CHIP_COUNT - 1), selectedCategory];
    return top;
  }, [categories, selectedCategory]);

  const search = catSearch.trim();
  const normalizedSearch = normalizeCategoryName(search);
  const searchMatches = search
    ? categories.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()) || normalizeCategoryName(c.name).includes(normalizedSearch))
    : [];
  const sameAsExisting = search ? categories.find((c) => normalizeCategoryName(c.name) === normalizedSearch) : undefined;

  const paymentMethods: PaymentMethodOption[] = useMemo(() => {
    const list = [...options.payment_methods];
    // Keep an older stored value selectable while editing
    if (method && !list.some((m) => m.value === method)) list.push({ value: method, label: paymentMethodLabel(method) });
    return list;
  }, [options.payment_methods, method]);

  const referenceField = referenceFieldFor(method);
  const amountNumber = parseFloat(amount.replace(/,/g, "")) || 0;
  const threshold = options.receipt_required_above;
  const receiptRequired =
    threshold > 0 && amountNumber > threshold && !receipt && !expense?.proof_path &&
    (!isEdit || amountNumber > Number(expense?.amount || 0));
  const matchedVendor = vendors.find((v) => v.name.trim().toLowerCase() === paidTo.trim().toLowerCase());
  const titlePreview = defaultExpenseTitle(selectedCategory?.name, paidTo);

  const eventOptions = useMemo(
    () => [
      { value: "", label: "Not for an event" },
      ...events.map((e) => ({ value: String(e.id), label: e.start_date ? `${e.name} · ${formatDate(e.start_date)}` : e.name })),
    ],
    [events]
  );

  const pickCategory = (cat: ExpenseCategoryOption) => {
    setCategoryId(cat.id);
    setCatSearch("");
    setCatListOpen(false);
    setErrors((e) => ({ ...e, category: "" }));
  };

  const createCategory = async (name: string) => {
    if (!name || creatingCategory) return;
    setCreatingCategory(true);
    try {
      const res = await expensesApi.createCategory({ name });
      const created: ExpenseCategoryOption = { ...res.data, expenses_count: 0 };
      setOptions((o) => ({ ...o, categories: [...o.categories, created] }));
      pickCategory(created);
      showToast(`Category “${created.name}” created`, "success");
    } catch (err) {
      const existing = (err as ApiError).data?.existing as ExpenseCategoryOption | undefined;
      if (existing) {
        if (!categories.some((c) => c.id === existing.id)) {
          setOptions((o) => ({ ...o, categories: [...o.categories, existing] }));
        }
        pickCategory(existing);
        showToast(`Using the existing “${existing.name}”`, "info");
      } else {
        showToast(apiErrorMessage(err, "Couldn't create the category"), "error");
      }
    } finally {
      setCreatingCategory(false);
    }
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (amountNumber <= 0) next.amount = "Enter an amount above ₹0";
    if (!date) next.date = "Pick a date";
    if (receiptRequired) next.receipt = `Attach a receipt for expenses above ${formatCurrencyFull(threshold)}`;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (addAnother: boolean) => {
    if (saving || !validate()) return;
    setSaving(true);
    try {
      const form = new FormData();
      // Every field is sent (empty = cleared) so edits can remove an event, category or note
      form.append("amount", String(amountNumber));
      form.append("expense_date", date);
      form.append("expense_category_id", categoryId ? String(categoryId) : "");
      form.append("event_id", eventId);
      form.append("payment_method", method || "");
      form.append("reference_number", referenceField ? reference.trim() : "");
      form.append("paid_to", paidTo.trim());
      form.append("vendor_id", matchedVendor ? String(matchedVendor.id) : "");
      form.append("description", note.trim());
      form.append("title", title.trim());
      form.append("notes", internalNotes.trim());
      if (receipt) form.append("proof_file", receipt);

      await expensesApi.save(expense?.id, form);
      showToast(
        `${isEdit ? "Updated" : "Saved"} ${formatCurrencyFull(amountNumber)} · ${selectedCategory?.name || "Uncategorised"}`,
        "success"
      );
      onExpenseSaved();

      if (addAnother && !isEdit) {
        // Keep the date, event and payment method for the next bill
        setAmount("");
        setCategoryId(null);
        setReference("");
        setPaidTo("");
        setReceipt(null);
        setNote("");
        setTitle("");
        setInternalNotes("");
        setErrors({});
        amountRef.current?.focus();
      } else {
        onClose();
      }
    } catch (err) {
      const fieldErrors: Record<string, string> = {};
      Object.entries((err as ApiError).errors || {}).forEach(([field, msgs]) => {
        fieldErrors[FIELD_KEYS[field] || field] = Array.isArray(msgs) ? msgs[0] : String(msgs);
      });
      setErrors(fieldErrors);
      showToast(apiErrorMessage(err, "Couldn't save the expense"), "error");
    } finally {
      setSaving(false);
    }
  };

  const chip = (active: boolean) =>
    `min-h-10 px-3.5 rounded-full border text-sm transition-colors ${
      active
        ? "border-amber-500 bg-amber-50 text-amber-800 font-semibold"
        : "border-stone-200 bg-white text-stone-600 hover:border-amber-300 hover:bg-amber-50/50"
    }`;
  const label = "block text-xs font-semibold text-stone-500 mb-1.5";
  const input = "w-full h-11 px-3 rounded-xl border border-stone-200 text-sm bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none";
  const fieldError = (key: string) => errors[key] ? <p className="text-xs text-red-600 mt-1">{errors[key]}</p> : null;

  const footer = (
    <div className="flex gap-2">
      {isEdit ? (
        <button type="button" onClick={onClose} className="flex-1 h-11 rounded-xl border border-stone-300 text-sm font-semibold text-stone-600 hover:bg-stone-50">
          Cancel
        </button>
      ) : (
        <button type="button" onClick={() => submit(true)} disabled={saving} className="flex-1 h-11 rounded-xl border border-stone-300 text-sm font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-50">
          Save &amp; add another
        </button>
      )}
      <button type="button" onClick={() => submit(false)} disabled={saving} className="flex-1 h-11 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center">
        {saving ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : isEdit ? "Save changes" : "Save expense"}
      </button>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? "Edit expense" : "Add expense"} size="lg" footer={footer}>
      <form onSubmit={(e) => { e.preventDefault(); submit(false); }} className="space-y-5">
        {/* Amount — the one thing everyone knows */}
        <div>
          <label htmlFor="expense-amount" className={label}>Amount</label>
          <div className={`flex items-baseline gap-2 border-b-2 pb-1 ${errors.amount ? "border-red-400" : "border-amber-500"}`}>
            <span className="text-2xl font-medium text-stone-400">₹</span>
            <input
              id="expense-amount"
              ref={amountRef}
              inputMode="decimal"
              autoComplete="off"
              value={amount}
              onChange={(e) => { setAmount(e.target.value.replace(/[^0-9.,]/g, "")); setErrors((x) => ({ ...x, amount: "" })); }}
              placeholder="0"
              className="w-full bg-transparent text-3xl font-bold text-stone-800 outline-none tabular-nums"
            />
          </div>
          {fieldError("amount")}
        </div>

        {/* Category: chips + search with inline create */}
        <div>
          <span className={label} id="expense-category-label">Paid for</span>
          {optionsLoading && categories.length === 0 ? (
            <div className="h-10 rounded-full bg-stone-100 animate-pulse w-2/3" />
          ) : (
            <div className="flex flex-wrap gap-2" role="group" aria-labelledby="expense-category-label">
              {chipCategories.map((c) => (
                <button key={c.id} type="button" aria-pressed={c.id === categoryId} onClick={() => (c.id === categoryId ? setCategoryId(null) : pickCategory(c))} className={chip(c.id === categoryId)}>
                  {c.name}
                </button>
              ))}
            </div>
          )}
          <div className="relative mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
            <input
              role="combobox"
              aria-expanded={catListOpen && !!search}
              aria-controls="expense-category-list"
              aria-label="Search or create a category"
              value={catSearch}
              onChange={(e) => { setCatSearch(e.target.value); setCatListOpen(true); }}
              onFocus={() => setCatListOpen(true)}
              onBlur={() => setTimeout(() => setCatListOpen(false), 150)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setCatListOpen(false);
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (searchMatches[0]) pickCategory(searchMatches[0]);
                  else if (search && !sameAsExisting) createCategory(search);
                }
              }}
              placeholder={categories.length ? "Search or create a category…" : "Type a category name to create it…"}
              className={`${input} pl-9`}
            />
            {catListOpen && search && (
              <ul id="expense-category-list" role="listbox" className="absolute z-20 left-0 right-0 mt-1 max-h-60 overflow-y-auto rounded-xl border border-stone-200 bg-white p-1 shadow-lg">
                {searchMatches.map((c) => (
                  <li key={c.id} role="option" aria-selected={c.id === categoryId}>
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pickCategory(c)} className="w-full min-h-10 px-3 rounded-lg flex items-center justify-between gap-3 text-sm text-left hover:bg-amber-50">
                      <span className="text-stone-800">{c.name}</span>
                      <span className="text-xs text-stone-400">{c.expenses_count ? `${c.expenses_count} used` : "new"}</span>
                    </button>
                  </li>
                ))}
                {sameAsExisting && sameAsExisting.name.toLowerCase() !== search.toLowerCase() && (
                  <li className="px-3 py-2 text-xs text-stone-500">“{search}” matches the existing “{sameAsExisting.name}”.</li>
                )}
                {!sameAsExisting && (
                  <li role="option" aria-selected={false}>
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => createCategory(search)} disabled={creatingCategory} className="w-full min-h-10 px-3 rounded-lg flex items-center gap-2 text-sm font-semibold text-amber-700 hover:bg-amber-50 disabled:opacity-50">
                      <Plus className="w-4 h-4" /> {creatingCategory ? "Creating…" : `Create “${search}”`}
                    </button>
                  </li>
                )}
              </ul>
            )}
          </div>
          {fieldError("category")}
        </div>

        {/* Date */}
        <div>
          <span className={label}>Date</span>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" aria-pressed={date === localToday()} onClick={() => setDate(localToday())} className={chip(date === localToday())}>Today</button>
            <button type="button" aria-pressed={date === localDateOffset(-1)} onClick={() => setDate(localDateOffset(-1))} className={chip(date === localDateOffset(-1))}>Yesterday</button>
            <input type="date" aria-label="Expense date" value={date} max={localToday()} onChange={(e) => setDate(e.target.value)} className={`${input} w-auto flex-1 min-w-[150px]`} />
          </div>
          {fieldError("date")}
        </div>

        {/* Event */}
        <div>
          <span className={label}>For event <span className="font-normal">(optional)</span></span>
          <SearchableDropdown options={eventOptions} value={eventId} onChange={setEventId} placeholder="Not for an event" />
          <p className="text-[11px] text-stone-400 mt-1">Linked expenses count in that event's profit.</p>
          {fieldError("event")}
        </div>

        {/* Paid via */}
        <div>
          <span className={label} id="expense-method-label">Paid via</span>
          <div className="flex flex-wrap gap-2" role="group" aria-labelledby="expense-method-label">
            {paymentMethods.map((m) => (
              <button key={m.value} type="button" aria-pressed={m.value === method} onClick={() => setMethod(m.value)}
                className={`min-h-10 px-4 rounded-xl border text-sm transition-colors ${m.value === method ? "border-amber-600 bg-amber-600 text-white font-semibold" : "border-stone-200 bg-white text-stone-600 hover:bg-stone-50"}`}>
                {m.label}
              </button>
            ))}
          </div>
          {fieldError("method")}
        </div>

        <div className={`grid gap-4 ${referenceField ? "sm:grid-cols-2" : ""}`}>
          {referenceField && (
            <div>
              <label htmlFor="expense-reference" className={label}>{referenceField.label}</label>
              <input id="expense-reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder={referenceField.placeholder} className={input} />
            </div>
          )}
          <div>
            <label htmlFor="expense-paid-to" className={label}>Paid to <span className="font-normal">(optional)</span></label>
            <input id="expense-paid-to" list="expense-vendor-names" value={paidTo} onChange={(e) => setPaidTo(e.target.value)} placeholder="Person or vendor" className={input} />
            <datalist id="expense-vendor-names">
              {vendors.map((v) => <option key={v.id} value={v.name} />)}
            </datalist>
            {matchedVendor && <p className="text-[11px] text-stone-400 mt-1">Linked to vendor {matchedVendor.name}. This doesn't change what you owe them.</p>}
          </div>
        </div>

        {/* Receipt */}
        <div>
          <span className={label}>
            Receipt
            {threshold > 0 && <span className="font-normal"> · required above {formatCurrencyFull(threshold)}</span>}
          </span>
          <ImageUpload selectedFile={receipt} onFileSelect={(f) => { setReceipt(f); setErrors((x) => ({ ...x, receipt: "" })); }} label="Take a photo or upload the receipt" maxSize={5 * 1024 * 1024} compressToWebP />
          {expense?.proof_path && !receipt && (
            <a href={getImageUrl(expense.proof_path)} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:underline">
              <Paperclip className="w-3.5 h-3.5" /> View the current receipt
            </a>
          )}
          {receiptRequired && !errors.receipt && <p className="text-xs text-amber-700 mt-1">A receipt is needed for this amount.</p>}
          {fieldError("receipt")}
        </div>

        {/* Note */}
        <div>
          <label htmlFor="expense-note" className={label}>Note <span className="font-normal">(optional)</span></label>
          <textarea id="expense-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="e.g. 35 L diesel for the generator, 6 hours" className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none" />
        </div>

        {/* Less-used fields */}
        <details className="group rounded-xl border border-stone-200" open={!!title || !!internalNotes}>
          <summary className="flex items-center justify-between cursor-pointer select-none min-h-11 px-3 text-sm font-medium text-stone-600">
            More details
            <ChevronDown className="w-4 h-4 text-stone-400 transition-transform group-open:rotate-180" />
          </summary>
          <div className="px-3 pb-3 space-y-4">
            <div>
              <label htmlFor="expense-title" className={label}>Title <span className="font-normal">(leave blank to use “{titlePreview}”)</span></label>
              <input id="expense-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={titlePreview} className={input} />
              {fieldError("title")}
            </div>
            <div>
              <label htmlFor="expense-internal" className={label}>Internal notes</label>
              <textarea id="expense-internal" value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} rows={2} placeholder="Only visible to staff" className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none resize-none" />
            </div>
          </div>
        </details>
      </form>
    </Modal>
  );
}
